// The crew app's frame (TZ 8, 15.3; STAGE_5 5.1), after docs/mockups/doctor_prototype.html:
// a top bar that only tells (the network and the actions waiting, the shift, the time),
// the screen, and four tabs at the bottom. Nothing to press up top: everything that is
// pressed sits in the lower part of the screen. Dark unless the crew chose light.

import {
  ClockCircleOutlined,
  HistoryOutlined,
  MedicineBoxOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Typography, theme } from 'antd';
import { Suspense, useCallback, useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router';

import { FullScreenSpin } from '@/components/FullScreenSpin';
import { SystemBanners } from '@/components/SystemBanners';
import { displayName, useCurrentUser } from '@/features/auth/authContext';
import { useAppConfig } from '@/features/config/configContext';
import { ru } from '@/i18n/ru';
import { dayjs } from '@/lib/dates';
import { FREE_COLOR, WARN_COLOR } from '@/lib/statusColors';
import { useThemeMode } from '@/theme/themeContext';

import { CREW_KEY, onLineFor, useDoctorCalls, useDoctorShift, vehicleName, type CrewOnLine } from './crewApi';
import { pushState, subscribeToPush } from './offline/push';
import { flush } from './offline/queue';
import { unaccepted } from './crewCallFormat';
import { NewCallScreen } from './NewCallScreen';
import { CREW_TABS, crewPath, type CrewTab } from './crewPaths';
import { CrewTheme, TAB_BAR, TOP_BAR, TOUCH } from './crewTheme';
import { useOnline, useQueuedActions } from './network';
import { useServiceWorker } from './serviceWorker';

const c = ru.crew;
/** The prototype's frame: a tablet in portrait, wider screens keep the same column. */
const FRAME_WIDTH = 820;
/** How often the queue tries again while something is still in it. */
const FLUSH_EVERY_MS = 30_000;

const TAB_ICONS: Record<CrewTab, ComponentType> = {
  calls: MedicineBoxOutlined,
  shift: ClockCircleOutlined,
  expense: WalletOutlined,
  history: HistoryOutlined,
};

/** "Online", or "Offline - 3 actions waiting": always in sight (TZ 15.3). */
export function NetworkStatus() {
  const online = useOnline();
  const queued = useQueuedActions();
  const text = ru.dispatch.join(online ? c.online : c.offline, ...(queued > 0 ? [c.waiting(queued)] : []));
  const color = online && queued === 0 ? FREE_COLOR : WARN_COLOR;
  return (
    <span role="status" data-online={online} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color }}>
      <span aria-hidden style={{ width: 10, height: 10, borderRadius: '50%', background: color }} />
      {text}
    </span>
  );
}

function Clock() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 10_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  return <span>{dayjs(now).format('HH:mm')}</span>;
}

/** "Vehicle 01 01A123BC - on line 3 h 12 min", or "<name> - not on line". */
function shiftLine(name: string, crew: CrewOnLine | null | undefined, now: number): string {
  if (!crew) return ru.dispatch.join(name, c.offLine);
  return c.shift.onLineFor(vehicleName(crew), onLineFor(crew.started_at, now));
}

function TopBar() {
  const user = useCurrentUser();
  const { token } = theme.useToken();
  const shift = useDoctorShift();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 30_000);
    return () => {
      window.clearInterval(timer);
    };
  }, []);
  return (
    <Flex
      align="center"
      gap={12}
      style={{
        height: TOP_BAR,
        padding: '0 16px',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
        fontSize: 15,
      }}
    >
      <NetworkStatus />
      <Typography.Text type="secondary" ellipsis style={{ flex: 1, fontSize: 15 }}>
        {shiftLine(displayName(user), shift.data?.crew, now)}
      </Typography.Text>
      <Clock />
    </Flex>
  );
}

function TabBar() {
  const { token } = theme.useToken();
  return (
    <nav
      aria-label={ru.nav.menu}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${String(CREW_TABS.length)}, 1fr)`,
        height: TAB_BAR,
        borderTop: `1px solid ${token.colorBorderSecondary}`,
        background: token.colorBgContainer,
      }}
    >
      {CREW_TABS.map((tab) => {
        const Icon = TAB_ICONS[tab];
        return (
          <NavLink
            key={tab}
            to={crewPath(tab)}
            style={({ isActive }) => ({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              minHeight: TOUCH,
              fontSize: 14,
              textDecoration: 'none',
              color: isActive ? token.colorPrimary : token.colorTextSecondary,
              fontWeight: isActive ? 600 : 400,
            })}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }}>
              <Icon />
            </span>
            {c.tabs[tab]}
          </NavLink>
        );
      })}
    </nav>
  );
}

function UpdateBar({ onUpdate }: { onUpdate: () => void }) {
  const { token } = theme.useToken();
  return (
    <Flex
      role="alert"
      align="center"
      gap={12}
      style={{ padding: '8px 16px', background: token.colorInfoBg, borderTop: `1px solid ${token.colorInfoBorder}` }}
    >
      <span style={{ flex: 1 }}>{c.update}</span>
      <Button type="primary" onClick={onUpdate}>
        {c.updateNow}
      </Button>
    </Flex>
  );
}

/** A new assignment takes the whole screen, whichever tab is open. */
function NewAssignment() {
  const onLine = Boolean(useDoctorShift().data?.crew);
  const calls = useDoctorCalls(onLine);
  const call = onLine ? unaccepted(calls.data) : undefined;
  return call ? <NewCallScreen key={call.id} call={call} /> : null;
}

/** The queue goes out as soon as there is a network, and is tried again now and then
 * while anything is left in it (STAGE_5 5.6). */
function useQueueFlush(): void {
  const waiting = useQueuedActions();
  const queryClient = useQueryClient();
  // Once queued actions have gone out, what the screens show is fetched again: they asked
  // on reconnecting, maybe before the actions arrived.
  const send = useCallback(() => {
    void flush().then((sent) => {
      if (sent > 0) void queryClient.invalidateQueries({ queryKey: [CREW_KEY] });
    });
  }, [queryClient]);
  useEffect(() => {
    send();
    window.addEventListener('online', send);
    return () => {
      window.removeEventListener('online', send);
    };
  }, [send]);
  useEffect(() => {
    if (waiting === 0) return;
    const timer = window.setInterval(send, FLUSH_EVERY_MS);
    return () => {
      window.clearInterval(timer);
    };
  }, [send, waiting]);
}

/** The tablet asks to be told about a new assignment, once it may (TZ 12). */
function usePushSubscription(): void {
  const vapidKey = useAppConfig().vapid_public_key;
  useEffect(() => {
    if (!vapidKey || pushState() !== 'granted') return;
    void subscribeToPush(vapidKey);
  }, [vapidKey]);
}

export function CrewLayout() {
  const { prefer } = useThemeMode();
  const { needsUpdate, update } = useServiceWorker();
  useQueueFlush();
  usePushSubscription();

  // A cab at night: dark unless the crew switched to light (TZ 15.3, STAGE_5 5.1).
  useEffect(() => {
    prefer('dark');
  }, [prefer]);

  return (
    <CrewTheme>
      <CrewFrame>
        <div>
          <TopBar />
          <SystemBanners />
        </div>
        <main style={{ minHeight: 0, overflow: 'hidden' }}>
          <Suspense fallback={<FullScreenSpin />}>
            <Outlet />
          </Suspense>
        </main>
        <div>
          {needsUpdate && <UpdateBar onUpdate={update} />}
          <TabBar />
        </div>
        <NewAssignment />
      </CrewFrame>
    </CrewTheme>
  );
}

function CrewFrame({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();
  return (
    <div style={{ background: token.colorBgLayout, minHeight: '100dvh' }}>
      <div
        data-crew-frame
        style={{
          maxWidth: FRAME_WIDTH,
          margin: '0 auto',
          height: '100dvh',
          display: 'grid',
          gridTemplateRows: 'auto minmax(0, 1fr) auto',
          background: token.colorBgContainer,
          color: token.colorText,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        {children}
      </div>
    </div>
  );
}
